'use client';

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Loader2} from 'lucide-react';
import {getFeed} from 'iq-companies/api';
import type {FeedExperienceItem} from 'iq-companies/types';
import {getFeedItemKey} from 'iq-companies/utils';
import {FilterBar, type DateRange} from '../filter_bar';
import {FeedSection} from './feed_section';

const DATE_RANGE_DAYS: Record<DateRange, number> = {'90d': 90, '30d': 30, all: Infinity};

// Real feed: pagination is server-driven (GET /v1/companies/feed?page=), gated per
// visitor and never cached (plan § 1a). Company/Role are also server-side filters
// (backend § buildPublicFeedQuery) — changing either refetches page 0 under the new
// filter rather than scanning already-loaded pages client-side, since the feed now
// spans every company (not just the handful with a CIG) and a match could be
// arbitrarily many pages deep. Date range stays a client-side filter over whatever's
// already loaded — unchanged from the original Phase 0 behavior. Dropdown *options*
// come from GET /v1/companies/feed/filters (the full company/role universe), not
// from items loaded into state. Scrolling near the bottom loads the next raw page
// via IntersectionObserver — ported (not imported) from
// frontend/src/components/dashboard/home/feed_section.tsx's load-more pattern,
// replacing Phase 0's manual "Load more" button. No Industry chip: the feed has no
// per-experience industry data (that's real only on /companies/all, plan §
// Workstream 1 item 1g).
export function FeedArea({
  initialItems,
  initialHasMore,
  companies,
  roles,
}: {
  initialItems: FeedExperienceItem[];
  initialHasMore: boolean;
  companies: string[];
  roles: string[];
}) {
  const [items, setItems] = useState(initialItems);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>('90d');
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);
  const requestIdRef = useRef(0);
  const skipNextRefetchRef = useRef(true);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const nextPage = page + 1;
      const {items: newItems, hasMore: more} = await getFeed(nextPage, {company, role});
      setItems((prev) => {
        // The backend paginates over rows sharing the same date without a fully stable
        // sort, so the same row can occasionally reappear across two pages — drop it
        // here to keep item keys unique.
        const seen = new Set(prev.map(getFeedItemKey));
        const deduped = newItems.filter((item) => !seen.has(getFeedItemKey(item)));
        return [...prev, ...deduped];
      });
      setPage(nextPage);
      setHasMore(more);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [hasMore, page, company, role]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      // Fires while the sentinel is still 600px below the viewport, so the next page
      // finishes loading before the user actually scrolls to the bottom.
      {rootMargin: '600px', threshold: 0},
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, hasMore]);

  // Company/Role changed — refetch page 0 under the new filter. Skipped on mount
  // since initialItems/initialHasMore already came from an unfiltered SSR fetch.
  useEffect(() => {
    if (skipNextRefetchRef.current) {
      skipNextRefetchRef.current = false;
      return;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setItems([]); // clear stale cards from the previous filter while the new page loads
    getFeed(0, {company, role}).then(({items: newItems, hasMore: more}) => {
      if (requestId !== requestIdRef.current) return; // superseded by a newer filter change
      setItems(newItems);
      setPage(0);
      setHasMore(more);
      setLoading(false);
    });
  }, [company, role]);

  const filtered = useMemo(() => {
    const now = Date.now();
    const rangeDays = DATE_RANGE_DAYS[dateRange];
    if (rangeDays === Infinity) return items;
    return items.filter((item) => {
      if (!item.interview_date) return true;
      const ageDays = (now - new Date(item.interview_date).getTime()) / 86_400_000;
      return ageDays <= rangeDays;
    });
  }, [items, dateRange]);

  const isFiltered = company !== null || role !== null || dateRange !== '90d';

  return (
    <div>
      <FilterBar
        companies={companies}
        roles={roles}
        company={company}
        role={role}
        dateRange={dateRange}
        isFiltered={isFiltered}
        onCompanyChange={setCompany}
        onRoleChange={setRole}
        onDateRangeChange={setDateRange}
        onReset={() => {
          setCompany(null);
          setRole(null);
          setDateRange('90d');
        }}
      />
      <div className='mt-6'>
        {loading && items.length === 0 ? (
          <div className='flex justify-center py-12'>
            <Loader2 className='h-5 w-5 animate-spin text-gray-400' />
          </div>
        ) : (
          <FeedSection items={filtered} />
        )}
      </div>
      {hasMore && (
        <div ref={sentinelRef} className='mt-8 flex justify-center py-4'>
          {loading && items.length > 0 && (
            <Loader2 className='h-5 w-5 animate-spin text-gray-400' />
          )}
        </div>
      )}
    </div>
  );
}
